/**
 * SSE integration for twin real-time updates.
 *
 * Listens on the existing /api/events SSE stream and filters for twin:*
 * event types. Dispatches React Query invalidations when twin data changes.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { twinKeys } from './useTwin';

type TwinSSEEventType =
  | 'twin:event_created'
  | 'twin:event_resolved'
  | 'twin:entity_updated'
  | 'twin:pruefstatus_updated'
  | 'twin:processing_started'
  | 'twin:processing_completed';

interface UseTwinSSEOptions {
  enabled?: boolean;
}

/**
 * Hook that listens to the existing SSE stream for twin-specific events
 * and invalidates React Query caches accordingly.
 */
export function useTwinSSE(options: UseTwinSSEOptions = {}) {
  const { enabled = true } = options;
  const queryClient = useQueryClient();
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;

  useEffect(() => {
    if (!enabled) return;

    const API_URL = import.meta.env.VITE_API_URL || '/api';
    const eventSource = new EventSource(`${API_URL}/events`, {
      withCredentials: true,
    });

    const handleTwinEvent = (type: TwinSSEEventType) => {
      const qc = queryClientRef.current;

      switch (type) {
        case 'twin:event_created':
        case 'twin:event_resolved':
          qc.invalidateQueries({ queryKey: twinKeys.events.all });
          qc.invalidateQueries({ queryKey: twinKeys.entities.all });
          qc.invalidateQueries({ queryKey: twinKeys.stats });
          qc.invalidateQueries({ queryKey: twinKeys.bootstrap });
          break;

        case 'twin:entity_updated':
          qc.invalidateQueries({ queryKey: twinKeys.entities.all });
          // Issue #637: ``lenseCounts`` is now keyed on the optional
          // search ``q``; invalidate by the 2-tuple prefix so every
          // variant (search-less + per-query) refetches.
          qc.invalidateQueries({ queryKey: ['twin', 'lenseCounts'] });
          qc.invalidateQueries({ queryKey: twinKeys.stats });
          // Codex review on PR #594 round 3 (P2): the FarmPage now
          // reads badge counts off bootstrap, so SSE must invalidate
          // the bootstrap query too — otherwise badges stay stale
          // until the 60s ``staleTime`` ages out.
          qc.invalidateQueries({ queryKey: twinKeys.bootstrap });
          break;

        case 'twin:pruefstatus_updated':
          qc.invalidateQueries({ queryKey: twinKeys.pruefstatus.all });
          qc.invalidateQueries({ queryKey: twinKeys.stats });
          break;

        case 'twin:processing_completed':
          // Full refresh after pipeline completes
          qc.invalidateQueries({ queryKey: twinKeys.all });
          break;
      }
    };

    // Listen for each twin event type
    const twinEventTypes: TwinSSEEventType[] = [
      'twin:event_created',
      'twin:event_resolved',
      'twin:entity_updated',
      'twin:pruefstatus_updated',
      'twin:processing_started',
      'twin:processing_completed',
    ];

    for (const eventType of twinEventTypes) {
      eventSource.addEventListener(eventType, () => {
        handleTwinEvent(eventType);
      });
    }

    return () => {
      eventSource.close();
    };
  }, [enabled]);
}
